import { ForwardedRef, forwardRef, useMemo } from 'react';

import { DawnCross } from '@xemplo/icons';
import { applyInputIconStrokeWidth, MaybeErrorOrDescription } from '@xemplo/input-utils';

import { Chips } from '../chips/chips';

import * as S from './select.styles';
import { SelectProps } from './select.types';
import { useSelectMeta } from './use-select-meta';

/**
 * A Select component that supports multi-selection with chips display.
 * This component simulates a select element but it's not a native select.
 * It uses a combination of divs and ARIA attributes to provide accessibility.
 *
 * @remarks This component is not intended to be used as a standalone component.
 * It's the base for building multi-select dropdowns with custom behavior.
 */
export const Select = forwardRef(
  (props: SelectProps, ref?: ForwardedRef<HTMLDivElement>) => {
    const { leadingIcon, hasValue, inputSize } = props;
    const {
      clearIconProps,
      labelProps,
      maybeErrorOrDescriptionProps,
      selectContainerProps,
      chipsProps,
      styles,
    } = useSelectMeta(props);

    const memoLeadingIcon = useMemo(
      () => applyInputIconStrokeWidth(leadingIcon, inputSize),
      [leadingIcon, inputSize],
    );

    const selectContent = useMemo(() => {
      if (hasValue) return <Chips {...chipsProps} />;
      if (props.placeholder) {
        return (
          <S.Placeholder className={styles.placeholder}>
            {props.placeholder}
          </S.Placeholder>
        );
      }
      return null;
    }, [hasValue, props.placeholder, styles.placeholder, chipsProps]);

    return (
      <S.Wrapper style={styles.width}>
        <S.Container ref={ref} {...selectContainerProps}>
          {memoLeadingIcon}
          <S.Select className={styles.select}>{selectContent}</S.Select>
          <S.Label {...labelProps}>{props.label}</S.Label>
          {hasValue && (
            <S.IconWrapper {...clearIconProps}>
              <DawnCross aria-hidden />
            </S.IconWrapper>
          )}
          <S.IconWrapper>
            <S.ArrowIcon className={styles.arrowIcon} aria-hidden />
          </S.IconWrapper>
        </S.Container>
        <MaybeErrorOrDescription {...maybeErrorOrDescriptionProps} />
      </S.Wrapper>
    );
  },
);

Select.displayName = 'Select';
export default Select;
